import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Button,
    CloseButton,
    Form,
    Modal,
    Spinner,
} from 'react-bootstrap';
import useRevealAlert from '@/shared/hooks/useRevealAlert';
import { decisionService } from '@/shared/services/decisionService';
import {
    usePreviewLock,
    PREVIEW_LOCK_NOTE_ID,
} from '@/shared/hooks/usePreviewLock';
import PreviewLockNote from '@/components/common/PreviewLockNote';
import { formatReleaseMoment } from '@/shared/utils/deadline';
import ActionBar from '@/components/common/ActionBar';
import DecisionEmailComposer, { MESSAGE_MAX } from './DecisionEmailComposer';
import './ConfirmDecisionModal.css';

// A program with no release date ahead releases a decision as it is recorded,
// and the server refuses to change a released one
const RELEASED_RULE =
    'Faculty see this decision as soon as you confirm, and it cannot be changed after that.';

// `verb` is the card button's label, which the confirm button repeats
const OUTCOMES = {
    approved: {
        verb: 'Approve',
        title: 'Approve this request?',
        variant: 'primary',
        consequence: RELEASED_RULE,
    },
    denied: {
        verb: 'Decline',
        title: 'Decline this request?',
        variant: 'danger',
        consequence: RELEASED_RULE,
    },
    waitlisted: {
        verb: 'Waitlist',
        title: 'Waitlist this request?',
        variant: 'primary',
        consequence:
            'You can still decline it later, or approve it unless another program awards support first.',
    },
    withdraw: {
        verb: 'Withdraw decision',
        title: 'Withdraw this decision?',
        variant: 'danger',
        consequence:
            "The request goes back to undecided for your program, and any nominee offers this decision made are removed. Faculty haven't seen this decision, so nobody is told.",
    },
    revision: {
        verb: 'Return for revision',
        title: 'Return this request for revision?',
        variant: 'warning',
        consequence:
            'It goes back to the applicant with your notes to edit and resubmit, and comes back for review once they do.',
    },
};

// What a decline does to the section's CRN. The server only frees it once
// the application ends up rejected (see freeCrnIfStatusFreesIt), which the
// page can be sure of only when this is the one program ranked
function declineCrnText(crnEffect, crn) {
    const theCrn = crn ? `CRN ${crn}` : "the section's CRN";
    if (crnEffect === 'frees') {
        return `This frees ${theCrn}, so another application can be filed for the section.`;
    }
    if (crnEffect === 'kept') {
        return `Another program has awarded support, so the application keeps ${theCrn}.`;
    }
    // A pending contingent award counts as awarded: this decline makes it final
    return `If no other program the applicant ranked is still reviewing it, has waitlisted it or has awarded support, this closes the application and frees ${theCrn} for another application.`;
}

// The server records an approval as a contingent award on the highest-ranked
// program above this one without a final decision; a waitlist or a pending
// contingent award isn't final (routingService.shouldBeContingent). With
// three programs, at most two can rank above this one
function contingentText(higherRanked) {
    const names = higherRanked.join(' and ');
    if (higherRanked.length === 1) {
        return `The applicant ranked ${names} higher. If ${names} hasn't made a final decision yet, this is recorded as a contingent award, which is voided if ${names} awards support.`;
    }
    return `The applicant ranked ${names} higher. If either hasn't made a final decision yet, this is recorded as a contingent award on the higher-ranked one still open, which is voided if that program awards support.`;
}

// What the faculty member sees, and what resubmitting does, under each way of
// returning a waitlisted application
const WAITLIST_CHOICES = {
    remove: {
        label: 'Take it off the waitlist',
        help: 'The waitlist decision is withdrawn and kept on record for coordinators and admins only. The faculty member sees an ordinary returned application, and when they resubmit it, the program reviews it fresh.',
    },
    keep: {
        label: 'Keep it on the waitlist',
        help: 'The waitlist decision stays. The faculty member sees the application returned with its ranking locked, and when they resubmit it, it goes back on the waitlist.',
    },
};

// Before the program releases its decisions, keeping the waitlist would show
// the faculty member that a program has decided
const HELD_WAITLIST_NOTE =
    "This program's decisions have not been released, so the application can be returned now only by taking it off the waitlist, or with its place kept once decisions are released.";

// The outcomes that send a decision email, which the dialog fills from the
// program's template
const EMAIL_OUTCOMES = ['approved', 'waitlisted', 'denied'];

// The last button of a decision that emails faculty says that it does
const NOTIFY_VERB = 'Confirm and notify faculty';

// When the decision email reaches faculty: the server holds it until the
// program's release moment (deliverAfter on decision.released)
function notifiedWhen(holds, releaseDate) {
    const released = formatReleaseMoment(releaseDate);
    return holds && released
        ? `When decisions are released on ${released}`
        : 'As soon as you confirm';
}

// Matches the server's limit on the reason for a change
const REASON_MAX = 1000;

// While the program holds its decisions faculty see none of them, so a
// decision can still be changed or withdrawn. Once released it can't: the
// rule the server's releasedRefusal enforces.
function consequenceFor(key, { changing, holds, releaseDate }) {
    const outcome = OUTCOMES[key];
    const released = formatReleaseMoment(releaseDate);
    if (key === 'withdraw' || key === 'revision' || !holds || !released) {
        return outcome.consequence;
    }
    if (changing) {
        return `You can change this again until decisions are released on ${released}. Faculty haven't seen it yet.`;
    }
    if (key === 'waitlisted') return outcome.consequence;
    return `You can change or withdraw this until decisions are released on ${released}. Faculty don't see it before then.`;
}

/**
 * Asks a coordinator to confirm Approve, Decline, Waitlist or Return for
 * Revision before anything is recorded (#528). While the program holds its
 * decisions (`holds`, with the stored `releaseDate`) an approval or a
 * decline can still be changed or withdrawn; once released it can't be
 * changed. `changing` confirms a change to the program's held
 * decision, described by `currentDecision`, or its withdrawal ('withdraw'),
 * and adds an optional reason that `extras.reason` carries.
 * `onConfirm(type, extras)` records it; a refusal shows here, and `onRefused`
 * hands it to the page once the dialog has closed, so the page reloads only
 * then. Returning an application the program has waitlisted asks whether it
 * stays on the waitlist, and `extras` is `{ waitlist: 'keep' | 'remove' }`;
 * `waitlistReleased` says whether keeping it can be offered.
 * A decision's `extras` is `{ messageToFaculty }`, the message box exactly as
 * it stands, or nothing when the program's text could not be loaded and
 * nothing was typed. `outcomes` lists the decisions the page allows, and the
 * coordinator can switch between them here; switching asks first when the
 * message was edited. A decision that emails faculty lists the award
 * (`awardText`, such as "2 of 3 LAs"), who the email goes to (`recipients`)
 * and when, and its last button is "Confirm and notify faculty".
 */
function ConfirmDecisionModal({
    type,
    applicantName,
    instructorName,
    courseLabel,
    crn,
    programLabel,
    crnEffect,
    higherRanked = [],
    applicationId,
    programType,
    awardedCount,
    awardText = null,
    recipients = null,
    outcomes = [],
    revisionNotes,
    waitlisted = false,
    waitlistReleased = false,
    holds = false,
    releaseDate = null,
    changing = false,
    currentDecision,
    onConfirm,
    onCancel,
    onRefused,
}) {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    // A role preview can't record anything, so say so up front
    const previewLocked = usePreviewLock();
    // The confirm button goes away on a refusal, so focus moves to the reason
    const errorRef = useRevealAlert(error);
    // Keep the last outcome so the dialog doesn't change while it fades out
    const [shownType, setShownType] = useState(type);
    // The outcome picked inside the dialog, if it differs from the button
    // that opened it
    const [selectedType, setSelectedType] = useState(null);
    if (type && type !== shownType) {
        setShownType(type);
        setSelectedType(null);
    }
    const current =
        shownType === 'revision' ? shownType : selectedType || shownType;
    const outcome = OUTCOMES[current];

    const [draft, setDraft] = useState(null);
    // The outcome `draft` was asked for; it lags `current` after Keep
    const [draftType, setDraftType] = useState(null);
    const [draftFailed, setDraftFailed] = useState(false);
    const [draftLoading, setDraftLoading] = useState(false);
    // The page's outcome whose first draft has settled; the dialog opens then
    const [openedType, setOpenedType] = useState(null);
    const [message, setMessage] = useState('');
    // What the box held when it was last filled from a template
    const [prefilled, setPrefilled] = useState('');
    // The outcome waiting on Replace or Keep, or null
    const [asking, setAsking] = useState(null);
    const askRef = useRevealAlert(asking);
    const messageRef = useRef(null);
    const draftRequest = useRef(0);
    const refocusMessage = useRef(false);
    // Nothing is chosen when the dialog opens: each way of returning it has
    // a different result for the faculty member
    const [waitlistChoice, setWaitlistChoice] = useState('');
    const [reason, setReason] = useState('');
    const asksAboutWaitlist = shownType === 'revision' && waitlisted;
    const waitlistOptions = waitlistReleased ? ['remove', 'keep'] : ['remove'];
    const sendsEmail = EMAIL_OUTCOMES.includes(current);
    const titleId = 'confirm-decision-title';
    const descriptionId = 'confirm-decision-description';

    const loadDraft = useCallback(
        async (draftFor, opening = false) => {
            const request = ++draftRequest.current;
            setDraftLoading(true);
            let answer = null;
            try {
                const result = await decisionService.getDecisionEmailDraft(
                    applicationId,
                    programType,
                    draftFor,
                    ...(awardedCount === undefined ? [] : [awardedCount])
                );
                if (typeof result?.message === 'string') answer = result;
            } catch (_err) {
                answer = null;
            }
            if (request !== draftRequest.current) return;
            const text = answer ? answer.message : '';
            setDraft(answer);
            setDraftType(draftFor);
            setDraftFailed(!answer);
            setMessage(text);
            setPrefilled(text);
            setDraftLoading(false);
            if (opening) setOpenedType(draftFor);
        },
        [applicationId, programType, awardedCount]
    );

    useEffect(() => {
        if (!EMAIL_OUTCOMES.includes(type)) {
            draftRequest.current += 1;
            return;
        }
        loadDraft(type, true);
    }, [type, loadDraft]);

    // The box is disabled while a draft loads, so it can only take focus back
    // afterwards
    useEffect(() => {
        if (!draftLoading && refocusMessage.current) {
            refocusMessage.current = false;
            messageRef.current?.focus();
        }
    }, [draftLoading]);

    const choose = (next) => {
        if (next === current) {
            setAsking(null);
            return;
        }
        if (message !== prefilled) {
            setAsking(next);
            return;
        }
        setAsking(null);
        setSelectedType(next);
        loadDraft(next);
    };

    const replaceMessage = () => {
        refocusMessage.current = true;
        setSelectedType(asking);
        setAsking(null);
        loadDraft(asking);
    };

    const keepMessage = () => {
        setSelectedType(asking);
        setAsking(null);
        messageRef.current?.focus();
    };

    const close = () => {
        if (submitting) return;
        onCancel();
    };

    const confirmExtras = () => {
        if (asksAboutWaitlist) return { waitlist: waitlistChoice };
        const why = changing && reason.trim() ? { reason: reason.trim() } : {};
        if (!sendsEmail) return changing ? why : undefined;
        // Sent even when empty: with no message the server fills in the
        // template, which a failed draft never showed the coordinator
        return { messageToFaculty: message, ...why };
    };

    const confirm = async () => {
        setSubmitting(true);
        try {
            await onConfirm(current, confirmExtras());
        } catch (err) {
            setError(
                err.response?.data?.message ||
                    'This could not be recorded. Please try again.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    // Fully closed: start the next opening clean
    const handleExited = () => {
        if (error) onRefused(error);
        setError(null);
        setWaitlistChoice('');
        setReason('');
        setSelectedType(null);
        setAsking(null);
        setDraft(null);
        setDraftType(null);
        setDraftFailed(false);
        setDraftLoading(false);
        setOpenedType(null);
        setMessage('');
        setPrefilled('');
    };

    return (
        <Modal
            show={
                !!OUTCOMES[type] &&
                (!EMAIL_OUTCOMES.includes(type) || openedType === type)
            }
            size="lg"
            // Header and footer stay on screen; only the body scrolls
            scrollable
            onHide={close}
            onExited={handleExited}
            // The page moves focus to the reason instead
            restoreFocus={!error}
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
        >
            {outcome && (
                <>
                    <Modal.Header>
                        <Modal.Title id={titleId} as="h2" className="h5">
                            {outcome.title}
                        </Modal.Title>
                        {/* Off while submitting, like Go back */}
                        <CloseButton
                            aria-label="Close"
                            onClick={close}
                            disabled={submitting}
                        />
                    </Modal.Header>
                    <Modal.Body>
                        <div id={descriptionId}>
                            <dl className="row mb-3">
                                <dt className="col-sm-4">Applicant</dt>
                                <dd className="col-sm-8">
                                    {applicantName || 'Not provided'}
                                </dd>
                                {/* Set only when filed on someone's behalf */}
                                {instructorName !== undefined && (
                                    <>
                                        <dt className="col-sm-4">
                                            Instructor of record
                                        </dt>
                                        <dd className="col-sm-8">
                                            {instructorName || 'Not named yet'}
                                        </dd>
                                    </>
                                )}
                                <dt className="col-sm-4">Course</dt>
                                <dd className="col-sm-8">
                                    {courseLabel}
                                    {crn && ` (CRN ${crn})`}
                                </dd>
                                <dt className="col-sm-4">Program</dt>
                                <dd className="col-sm-8">{programLabel}</dd>
                                {changing && (
                                    <>
                                        <dt className="col-sm-4">
                                            Current decision
                                        </dt>
                                        <dd className="col-sm-8">
                                            {currentDecision}
                                        </dd>
                                    </>
                                )}
                                <dt className="col-sm-4">
                                    {changing ? 'Change to' : 'Decision'}
                                </dt>
                                <dd className="col-sm-8">{outcome.verb}</dd>
                                {current === 'approved' && awardText && (
                                    <>
                                        <dt className="col-sm-4">Award</dt>
                                        <dd className="col-sm-8 fw-semibold">
                                            {awardText}
                                        </dd>
                                    </>
                                )}
                                {sendsEmail && recipients && (
                                    <>
                                        <dt className="col-sm-4">Email to</dt>
                                        <dd className="col-sm-8">
                                            {recipients}
                                        </dd>
                                    </>
                                )}
                                {sendsEmail && (
                                    <>
                                        <dt className="col-sm-4">
                                            Faculty notified
                                        </dt>
                                        <dd className="col-sm-8">
                                            {notifiedWhen(holds, releaseDate)}
                                        </dd>
                                    </>
                                )}
                                {current === 'revision' && (
                                    <>
                                        <dt className="col-sm-4">
                                            Revision notes
                                        </dt>
                                        <dd className="col-sm-8 text-break confirm-decision-text">
                                            {revisionNotes}
                                        </dd>
                                    </>
                                )}
                            </dl>
                            {current === 'denied' && (
                                <p>{declineCrnText(crnEffect, crn)}</p>
                            )}
                            {current === 'approved' &&
                                higherRanked.length > 0 && (
                                    <p>{contingentText(higherRanked)}</p>
                                )}
                            <p className={asksAboutWaitlist ? '' : 'mb-0'}>
                                {consequenceFor(current, {
                                    changing,
                                    holds,
                                    releaseDate,
                                })}
                            </p>
                            {asksAboutWaitlist && (
                                <fieldset className="mb-0">
                                    <legend className="h6">
                                        What happens to the waitlist?
                                    </legend>
                                    {!waitlistReleased && (
                                        <p>{HELD_WAITLIST_NOTE}</p>
                                    )}
                                    {waitlistOptions.map((key) => (
                                        <div key={key} className="mb-2">
                                            <Form.Check
                                                type="radio"
                                                name="waitlist-choice"
                                                id={`waitlist-choice-${key}`}
                                                label={
                                                    WAITLIST_CHOICES[key].label
                                                }
                                                value={key}
                                                checked={waitlistChoice === key}
                                                onChange={() =>
                                                    setWaitlistChoice(key)
                                                }
                                                disabled={submitting}
                                                aria-describedby={`waitlist-choice-${key}-help`}
                                            />
                                            <div
                                                id={`waitlist-choice-${key}-help`}
                                                className="form-text ms-4 mt-0"
                                            >
                                                {WAITLIST_CHOICES[key].help}
                                            </div>
                                        </div>
                                    ))}
                                </fieldset>
                            )}
                        </div>
                        {outcomes.length > 1 && sendsEmail && (
                            <fieldset className="mt-3">
                                <legend className="h6">
                                    Change the decision
                                </legend>
                                {outcomes.map((key) => (
                                    <Form.Check
                                        key={key}
                                        inline
                                        type="radio"
                                        name="decision-outcome"
                                        id={`decision-outcome-${key}`}
                                        label={OUTCOMES[key].verb}
                                        value={key}
                                        checked={current === key}
                                        onChange={() => choose(key)}
                                        disabled={submitting}
                                    />
                                ))}
                            </fieldset>
                        )}
                        {changing && (
                            <Form.Group
                                className="mt-3"
                                controlId="confirm-decision-reason"
                            >
                                <Form.Label className="h6">
                                    Reason for the change (optional)
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    maxLength={REASON_MAX}
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    disabled={submitting}
                                    aria-describedby="confirm-decision-reason-help"
                                />
                                <Form.Text id="confirm-decision-reason-help">
                                    Kept with the decision&apos;s history for
                                    coordinators and admins. Faculty never see
                                    it.
                                </Form.Text>
                            </Form.Group>
                        )}
                        {asking && (
                            <Alert
                                variant="warning"
                                className="mt-3 mb-0"
                                ref={askRef}
                                tabIndex={-1}
                            >
                                <p>
                                    {`You've changed the message. Replace it with the ${OUTCOMES[asking].verb} template?`}
                                </p>
                                <div className="d-flex gap-2 flex-wrap">
                                    <Button
                                        variant="outline-dark"
                                        size="sm"
                                        onClick={replaceMessage}
                                    >
                                        Replace message
                                    </Button>
                                    <Button
                                        variant="outline-dark"
                                        size="sm"
                                        onClick={keepMessage}
                                    >
                                        Keep my message
                                    </Button>
                                </div>
                            </Alert>
                        )}
                        {sendsEmail && (
                            <>
                                {draftFailed && !draftLoading && (
                                    <Alert
                                        variant="warning"
                                        className="mt-3 mb-0"
                                    >
                                        The program&apos;s email text
                                        couldn&apos;t be loaded. Faculty will
                                        get only the sentence that states the
                                        decision unless you write a message
                                        here.
                                    </Alert>
                                )}
                                <DecisionEmailComposer
                                    applicationId={applicationId}
                                    programType={programType}
                                    type={draftLoading ? draftType : current}
                                    awardedCount={awardedCount}
                                    draft={draftType === current ? draft : null}
                                    message={message}
                                    onMessageChange={setMessage}
                                    disabled={submitting}
                                    loading={draftLoading}
                                    messageRef={messageRef}
                                />
                            </>
                        )}
                        {error && (
                            <Alert
                                variant="danger"
                                className="mt-3 mb-0"
                                ref={errorRef}
                                tabIndex={-1}
                            >
                                {error}
                            </Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <PreviewLockNote locked={previewLocked} />
                        <ActionBar
                            className="w-100"
                            secondary={
                                <Button
                                    variant="outline-primary"
                                    onClick={close}
                                    disabled={submitting}
                                >
                                    Go back
                                </Button>
                            }
                            primary={
                                // The page reloads after a refusal, so the
                                // coordinator decides again from what it
                                // shows then
                                !error && (
                                    <Button
                                        variant={outcome.variant}
                                        onClick={confirm}
                                        disabled={
                                            submitting ||
                                            previewLocked ||
                                            draftLoading ||
                                            !!asking ||
                                            message.length > MESSAGE_MAX ||
                                            (asksAboutWaitlist &&
                                                !waitlistChoice)
                                        }
                                        aria-describedby={
                                            previewLocked
                                                ? PREVIEW_LOCK_NOTE_ID
                                                : undefined
                                        }
                                    >
                                        {submitting ? (
                                            <>
                                                <Spinner
                                                    animation="border"
                                                    size="sm"
                                                    className="me-2"
                                                    aria-hidden="true"
                                                />
                                                Submitting…
                                            </>
                                        ) : sendsEmail ? (
                                            NOTIFY_VERB
                                        ) : (
                                            outcome.verb
                                        )}
                                    </Button>
                                )
                            }
                        />
                    </Modal.Footer>
                </>
            )}
        </Modal>
    );
}

export default ConfirmDecisionModal;
